import get from "lodash/get";
import { resolveOrderType } from "util/pathwayOrderUtils";
import { STOP_STATUS_PALETTE } from "util/stopStatusUtils";

const INBOUND_PERMISSION_PATHS = [
  "permissions.orders.show_warehouse_data",
  "permissions.orders.show_hybrid_data",
  "permissions.Orders.show_warehouse_data",
  "permissions.Orders.show_hybrid_data",
];

const isPermissionEnabled = (value) =>
  value === true || value === 1 || value === "1" || value === "true";

export const getDeliveryTourStartId = (order) =>
  order?.Customer?.Tour?.ToursDeliveryTimings?.[0]?.supplier_location_id || null;

export const getOrderPickupOriginId = (order, user) =>
  order?.supplier_location_id ??
  order?.pickup_origin_id ??
  order?.pickup_origin ??
  order?.pickup_start ??
  user?.supplier_location_id ??
  null;

export const isReturnOrder = (order) => resolveOrderType(order) === "return";

export const isHandoverOrder = (order, user) => {
  const deliveryTourStart = getDeliveryTourStartId(order);
  const pickupOrigin = getOrderPickupOriginId(order, user);
  return Boolean(deliveryTourStart && pickupOrigin && deliveryTourStart !== pickupOrigin);
};

export const hasActiveHandover = (order) =>
  !!(
    order?.handover_id ||
    order?.Handover?.id ||
    order?.handover?.id ||
    order?.Handover ||
    order?.handover
  );

/** Warehouse / hybrid users who see inbound handover orders. */
export const canViewInboundHandoverOrders = (user) => {
  if (!user) {
    return false;
  }

  if (INBOUND_PERMISSION_PATHS.some((path) => isPermissionEnabled(get(user, path)))) {
    return true;
  }

  const ordersPerms = user?.permissions?.orders || user?.permissions?.Orders;
  return (
    isPermissionEnabled(ordersPerms?.show_warehouse_data) ||
    isPermissionEnabled(ordersPerms?.show_hybrid_data)
  );
};

const getPickupRoute = (order) =>
  order?.PickupRoute || order?.pickup_route || order?.Handover?.PickupRoute || order?.handover?.PickupRoute || null;

/** Pickup route on the order — `order.Route.start_date` indicates tour started. */
export const getOrderRoute = (order) => {
  const candidates = [
    order?.Route,
    order?.route,
    order?.Handover?.Route,
    order?.handover?.Route,
    order?.Handover?.PickupTour?.Route,
    order?.PickupTour?.Route,
    getPickupRoute(order),
  ];

  return candidates.find((route) => route && typeof route === "object") || null;
};

const isTruthyTimestamp = (value) => value != null && value !== false && value !== "";

const hasRouteStartDate = (route) => {
  if (!route) {
    return false;
  }
  const startDate = route.start_date ?? route.startDate;
  return startDate != null && startDate !== false && startDate !== "";
};

const hasRouteDeliveredAt = (route) => {
  if (!route) {
    return false;
  }
  const deliveredAt = route.delivered_at ?? route.deliveredAt;
  return isTruthyTimestamp(deliveredAt);
};

/** Route started and completed — hide icon (delivered at warehouse). */
const isRouteStartedAndDelivered = (order) => {
  const route = getOrderRoute(order);
  if (!route) {
    return false;
  }
  return hasRouteStartDate(route) && hasRouteDeliveredAt(route);
};

export const isPickupStopCompletedAtWarehouse = (order, user) => {
  if (isRouteStartedAndDelivered(order)) {
    return true;
  }

  const route = getOrderRoute(order);
  if (route && hasRouteStartDate(route) && isTruthyTimestamp(order?.delivered_at)) {
    return true;
  }

  if (
    isTruthyTimestamp(order?.pickup_stop_completed_at) ||
    isTruthyTimestamp(order?.pickup_stop_delivered_at) ||
    order?.pickup_stop_completed === true
  ) {
    return true;
  }

  const pickupRoute = getPickupRoute(order);

  if (pickupRoute && hasRouteStartDate(pickupRoute) && hasRouteDeliveredAt(pickupRoute)) {
    return true;
  }

  if (
    isTruthyTimestamp(pickupRoute?.pickup_stop_completed_at) ||
    pickupRoute?.pickup_stop_completed === true
  ) {
    return true;
  }

  const destinationId = getDeliveryTourStartId(order) ?? user?.supplier_location_id;
  const stops = pickupRoute?.pathway || pickupRoute?.Stops || [];
  const warehouseStop = stops.find((stop) => {
    if (stop?.route_type === "pickup_warehouse" || stop?.route_type === "end_point") {
      return true;
    }
    return destinationId != null && stop?.supplier_location_id === destinationId;
  });

  if (!warehouseStop) {
    return false;
  }

  if (isTruthyTimestamp(warehouseStop?.delivered_at)) {
    return true;
  }

  const stopOrders = warehouseStop?.Orders || [];
  return stopOrders.length > 0 && stopOrders.every((o) => isTruthyTimestamp(o?.delivered_at));
};

/** Driver started pickup tour — `order.Route.start_date` set. */
export const isPickupRouteStarted = (order) => {
  if (hasRouteStartDate(getOrderRoute(order))) {
    return true;
  }

  return (
    isTruthyTimestamp(order?.pickup_route_start_date) ||
    isTruthyTimestamp(order?.pickup_route_started_at)
  );
};

const normalizeApiInboundStatus = (raw) => {
  if (raw == null || raw === "") {
    return null;
  }

  const normalized = String(raw).toUpperCase().replace(/\s+/g, "_");

  if (["DELIVERED", "COMPLETE", "COMPLETED", "DONE"].includes(normalized)) {
    return "DELIVERED";
  }
  if (["IN_TRANSIT", "IN_PROGRESS", "STARTED", "ON_THE_WAY", "ACTIVE"].includes(normalized)) {
    return "IN_TRANSIT";
  }
  if (["NOT_STARTED", "PENDING", "OPEN", "CREATED"].includes(normalized)) {
    return "NOT_STARTED";
  }

  return null;
};

/** @returns {'NOT_STARTED'|'IN_TRANSIT'|'DELIVERED'|null} */
export const getInboundHandoverStatus = (order, user) => {
  if (isReturnOrder(order) || !isHandoverOrder(order, user) || !hasActiveHandover(order)) {
    return null;
  }

  if (isPickupStopCompletedAtWarehouse(order, user)) {
    return "DELIVERED";
  }

  if (isPickupRouteStarted(order)) {
    return "IN_TRANSIT";
  }

  const apiStatus = normalizeApiInboundStatus(
    order?.inbound_handover_status ?? order?.handover_pickup_status
  );
  if (apiStatus) {
    return apiStatus;
  }

  return "NOT_STARTED";
};

export const isInboundHandoverOrderForUser = (order, user) => {
  if (
    isReturnOrder(order) ||
    !isHandoverOrder(order, user) ||
    !hasActiveHandover(order) ||
    !canViewInboundHandoverOrders(user)
  ) {
    return false;
  }

  const destinationId = getDeliveryTourStartId(order);
  return destinationId != null && String(destinationId) === String(user?.supplier_location_id);
};

/** Grey / orange handshake for inbound rows; hidden when delivered. */
export const shouldShowInboundHandoverIcon = (order, user) => {
  if (isReturnOrder(order) || !canViewInboundHandoverOrders(user)) {
    return false;
  }

  if (!isHandoverOrder(order, user) || !hasActiveHandover(order)) {
    return false;
  }

  const status = getInboundHandoverStatus(order, user);
  if (!status || status === "DELIVERED") {
    return false;
  }

  const destinationId = getDeliveryTourStartId(order);
  if (destinationId == null) {
    return false;
  }

  return String(destinationId) === String(user?.supplier_location_id);
};

export const getInboundHandoverVisualMeta = (status, t) => {
  if (status === "IN_TRANSIT") {
    return {
      color: STOP_STATUS_PALETTE.orange,
      label: t("Handover In Progress..."),
    };
  }
  if (status === "NOT_STARTED") {
    return {
      color: STOP_STATUS_PALETTE.gray,
      label: t("Handover not started"),
    };
  }
  return null;
};
